import React from 'react';
import styled from 'styled-components';
import { Button, Dropdown, token } from '@bynder/design-system';
import { IconSmart } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { useDropdownNavigableTrigger } from './useDropdownNavigableTrigger';
import { useSearchMode, useSpotlightDropdownContext } from '../SpotlightState';

export function SearchModeDropdown() {
	const { isDropdownOpen } = useSpotlightDropdownContext();
	const { isNLSSearchOn, isHybridSearchEnabled, setIsNLSSearchOn } = useSearchMode();
	const { translate } = useTranslate();
	const { isActive, onOpen, onClose, getStableTriggerRef } =
		useDropdownNavigableTrigger<HTMLElement>(isDropdownOpen);

	const aiPoweredSubtitleKey = isHybridSearchEnabled
		? 'spotlight.searchModeDropdown.option.aiPowered.subtitleHybrid'
		: 'spotlight.searchModeDropdown.option.aiPowered.subtitle';

	const triggerLabel = isNLSSearchOn
		? translate('spotlight.searchModeDropdown.trigger.aiPowered')
		: translate('spotlight.searchModeDropdown.trigger.keyword');

	return (
		<Dropdown
			onOpen={onOpen}
			onClose={onClose}
			trigger={({ ref, ...triggerProps }, { open, close }) => (
				<StyledTriggerWrapper
					$isNLSSearchOn={isNLSSearchOn}
					$isActive={isActive}
					{...triggerProps}
					ref={getStableTriggerRef({ open, close, ref })}
				>
					<Button
						variant="clean"
						isSmall
						icon={isNLSSearchOn ? <IconSmart size="16px" /> : undefined}
						rightIcon={<Dropdown.Arrow />}
					>
						{triggerLabel}
					</Button>
				</StyledTriggerWrapper>
			)}
			position="bottom-right"
			minWidth="480px"
		>
			<Dropdown.ListItem
				role="menuitemradio"
				isChecked={!isNLSSearchOn}
				subtext={translate('spotlight.searchModeDropdown.option.keyword.subtitle')}
				onClick={() => setIsNLSSearchOn(false)}
			>
				{translate('spotlight.searchModeDropdown.option.keyword.title')}
			</Dropdown.ListItem>

			<Dropdown.ListItem
				role="menuitemradio"
				isChecked={isNLSSearchOn}
				subtext={translate(aiPoweredSubtitleKey)}
				onClick={() => setIsNLSSearchOn(true)}
			>
				{translate('spotlight.searchModeDropdown.option.aiPowered.title')}
			</Dropdown.ListItem>
		</Dropdown>
	);
}

const StyledTriggerWrapper = styled.div<{ $isNLSSearchOn: boolean; $isActive: boolean }>`
	display: inline-flex;
	border-radius: ${token.radiusPill};
	background-color: ${({ $isNLSSearchOn }) => ($isNLSSearchOn ? token.purple50 : token.gray50)};
	outline: ${({ $isActive }) => ($isActive ? `2px solid ${token.blue400}` : 'none')} !important;

	// TODO(DISCO-5719): overriding the DS Button's internal styles here is a quick workaround for Bynder Connect, not a real fix — there's
	// no supported DS prop for this that matches the AI-powered purple background + pill shape this trigger needs.
	// Revisit once it has been discussed with the Design team to see if we drop this requirement or if the DS adds a supported way to theme/override Button.

	&& > button,
	&& > button:hover,
	&& > button:active {
		border-radius: ${token.radiusPill};
		background-color: transparent;
		box-shadow: none;
		color: ${({ $isNLSSearchOn }) => ($isNLSSearchOn ? token.purple500 : token.colorText)};
		outline: none !important;
	}

	&& > button:focus {
		outline: none !important;
	}
`;
